import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
} from "@win-reality/react-ui";
import { useState } from "react";

import { CreatePlayersModal } from "@/components/CreatePlayersModal";
import UploadNewPlayersModal from "@/components/upload-new-players-modal";
import { parseUploadedMembers } from "@/utils/uploadedPlayers";

import { uploadedPlayerToLineUpPlayer } from "../../api/omnibusMappers";
import { useTaggingTheme } from "../../hooks/useTaggingTheme";
import { LineUpPlayer } from "../../types/gameFull";
import { ThemedDialog } from "../ThemedDialog";
import { OrgPlayerSearch } from "./OrgPlayerSearch";
import { PlayersToAdd } from "./PlayersToAdd";

type AddPlayersDialogProps = {
  /** Players already on the team's roster: left out of the search */
  rosterPlayerIds: Set<string>;
  /** The roster's org ("ORG#…"): players are searched for and created in it */
  orgId?: string;
  onClose: () => void;
  /** Adds the players to the roster (and, for the lineup panel, the batting order) */
  onAddPlayers: (players: LineUpPlayer[]) => void;
  /** Submit label */
  actionLabel?: string;
};

/**
 * Add Players modal: pick players from the roster's org, or create net new
 * ones there (Create Players / Bulk Upload, the same modals as Manage
 * Rosters). New players are created in omnibus right away, so every player
 * handed to `onAddPlayers` has a real id.
 */
export const AddPlayersDialog = ({
  rosterPlayerIds,
  orgId,
  onClose,
  onAddPlayers,
  actionLabel = "Add to Lineup",
}: AddPlayersDialogProps) => {
  const { theme } = useTaggingTheme();
  const queryClient = useQueryClient();
  const [players, setPlayers] = useState<LineUpPlayer[]>([]);
  const [createMode, setCreateMode] = useState<"manual" | "upload" | null>(
    null,
  );

  const excludedPlayerIds = new Set([
    ...rosterPlayerIds,
    ...players.map((p) => p.playerId),
  ]);

  const addPicked = (picked: LineUpPlayer[]) =>
    setPlayers((prev) => {
      const ids = new Set(prev.map((p) => p.playerId));
      return [...prev, ...picked.filter((p) => !ids.has(p.playerId))];
    });

  const handleCreated = (_externalIds: string[], members: unknown[]) => {
    addPicked(parseUploadedMembers(members).map(uploadedPlayerToLineUpPlayer));
    // So they show up in the search if they're removed from the list
    void queryClient.invalidateQueries({
      predicate: (query) =>
        (query.queryKey[0] as { _id?: string } | undefined)?._id ===
        "getPlayersSearch",
    });
  };

  const handleSubmit = () => {
    onAddPlayers(players);
    onClose();
  };

  return (
    <>
      <ThemedDialog
        open
        onOpenChange={(open) => !open && onClose()}
        defaultCancelButton={false}
        size="md"
      >
        {({ showDivider, setScrollContainer }) => (
          <>
            <DialogScrollableContent setScrollContainer={setScrollContainer}>
              <DialogHeader>
                <DialogTitle>Add Players</DialogTitle>
              </DialogHeader>

              <div className="space-y-5 pb-2">
                {orgId ? (
                  <OrgPlayerSearch
                    orgId={orgId}
                    excludedPlayerIds={excludedPlayerIds}
                    onAdd={(player) => addPicked([player])}
                  />
                ) : (
                  <div className="text-sm text-tagging-muted">
                    This roster has no organization to add players from.
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="success-outline"
                    size="sm"
                    isDisabled={!orgId}
                    onClick={() => setCreateMode("manual")}
                    data-testid="lineup-create-players-button"
                    className="rounded-full"
                  >
                    + Create Players
                  </Button>
                  <Button
                    variant="secondary-flat-dark-outline"
                    size="sm"
                    isDisabled={!orgId}
                    onClick={() => setCreateMode("upload")}
                    data-testid="lineup-bulk-upload-button"
                    className="rounded-full"
                  >
                    Bulk Upload
                  </Button>
                </div>

                <hr className="border-tagging-option-border" />

                <PlayersToAdd
                  players={players}
                  itemTestId="lineup-player-to-add-item"
                  removeButtonTestId="lineup-player-to-add-remove-button"
                  onRemove={(player) =>
                    setPlayers((prev) =>
                      prev.filter((p) => p.playerId !== player.playerId),
                    )
                  }
                />
              </div>
            </DialogScrollableContent>

            <DialogFooter showDivider={showDivider}>
              <div className="flex w-full items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={onClose}
                  data-testid="lineup-add-players-dialog-cancel-button"
                  className="underline"
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary-flat-light"
                  onClick={handleSubmit}
                  isDisabled={players.length === 0}
                  data-testid="lineup-add-players-dialog-confirm-button"
                  className="min-w-[168px] rounded-full"
                >
                  {actionLabel}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </ThemedDialog>

      {orgId && createMode === "manual" && (
        <CreatePlayersModal
          open
          onOpenChange={(open) => !open && setCreateMode(null)}
          orgId={orgId}
          onPlayersCreated={handleCreated}
          successToastMessage="Players created. Review and add them."
          variant={theme}
        />
      )}
      {orgId && createMode === "upload" && (
        <UploadNewPlayersModal
          open
          onOpenChange={(open) => !open && setCreateMode(null)}
          orgId={orgId}
          // The roster add happens on submit, with the picked players
          preSelectedRosterId={null}
          onSubmit={() => setCreateMode(null)}
          onPlayersUploaded={handleCreated}
          variant={theme}
        />
      )}
    </>
  );
};
